import "react-datepicker/dist/react-datepicker.css";

import { PROFILE_PICTURE_UPLOAD_FAILED_MESSAGE, SOMETHING_WENT_WRONG_MESSAGE, TRY_UPDATE_CHANGES_AGAIN_MESSAGE, UPDATE_SUCCESS_MESSAGE } from '../../common/constants';
import { formatDateForAPI, getUserInfoAndToken, getYearFromDate, parseDate } from '../../common/helper';
import { updateProfile, uploadProfilePicture, verifyEmail } from '../../services/ProfileService';
import { useEffect, useState, useRef } from 'react';

import Button from '../../components/button/Button';
import DatePicker from "react-datepicker";
import Loader from '../../components/loader/Loader';
import Select from 'react-select';
import TextFieldError from './TextFieldError';
import TextFieldSuccess from './TextFieldSuccess';
import Toast from '../../components/toast/Toast';
import analytics from '../../firebase';
import { logEvent } from "firebase/analytics";
import { useNavigate } from 'react-router-dom';

const AboutMe = ({ user, setUser, codeTableEmploymentStatusOptions, codeTableHighestEducationOptions, codeTableGenderOptions }) => {
    const [profilePicture, setProfilePicture] = useState(null);
    const [profilePictureUrl, setProfilePictureUrl] = useState('');
    const imgRef = useRef(null);
    const [name, setName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneNumberVerified, setPhoneNumberVerified] = useState(false);
    const [email, setEmail] = useState('');
    const [emailVerified, setEmailVerified] = useState(false);
    const [selectedEmploymentStatus, setSelectedEmploymentStatus] = useState({});
    const [selectedHighestEducation, setSelectedHighestEducation] = useState({});
    const [graduationYear, setGraduationYear] = useState('');
    const [graduationYearErrorMessage, setGraduationYearErrorMessage] = useState('');
    const [gender, setGender] = useState('');
    const [dob, setDob] = useState('');
    const [dobBackDateErrorMessage, setDobBackDateError] = useState('');
    const [selfIntro, setSelfIntro] = useState('');
    const [enableUpdateChangesBtn, setEnableUpdateChangesBtn] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessageHeader, setErrorMessageHeader] = useState('');
    const [errorMessageDescription, setErrorMessageDescription] = useState('');
    const [successMessageHeader, setSuccessMessageHeader] = useState('');
    const [successMessageDescription, setSuccessMessageDescription] = useState('');
    const storedUserInfo = JSON.parse(localStorage.getItem('user'));

    const navigate = useNavigate();

    const currentDate = new Date();
    const minGraduationYear = new Date(currentDate.getFullYear() - 60, currentDate.getMonth(), currentDate.getDate());
    const maxGraduationYear = new Date(currentDate.getFullYear() + 10, currentDate.getMonth(), currentDate.getDate());

    useEffect(() => {
        setProfilePictureUrl(`${user.pic}&cb=${new Date().getTime()}`);
        setName(user.fullm);
        setPhoneNumber(user.mobm);
        setPhoneNumberVerified(user.phverifd === '1');
        setEmail(user.eml);
        setEmailVerified(user.emlverifd === '1');

        const es = codeTableEmploymentStatusOptions.filter((es) => es.codeid === parseInt(user.empsts));
        if (es.length > 0) {
            setSelectedEmploymentStatus(es[0]);
        }

        const he = codeTableHighestEducationOptions.filter((he) => he.codeid === parseInt(user.edulvlc));
        if (he.length > 0) {
            setSelectedHighestEducation(he[0]);
        }

        setGraduationYear(user.eduyoc);

        const gv = codeTableGenderOptions.filter((g) => g.codeid === parseInt(user.gender));
        if (gv.length > 0) {
            setGender(gv[0]?.val);
        }

        setDob(user.brthdt);
        setSelfIntro(user.dsc);
    }, [user, setUser, codeTableEmploymentStatusOptions, codeTableHighestEducationOptions, codeTableGenderOptions])

    const handleProfilePictureChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfilePicture(file);

            const url = URL.createObjectURL(file);
            setProfilePictureUrl(url);

            setEnableUpdateChangesBtn(true);
        }
    }

    const handleNameChange = (e) => {
        setName(e.target.value);
        setEnableUpdateChangesBtn(true);
    }

    const handlePhoneNumberChange = (e) => {
        setPhoneNumber(e.target.value);
        setEnableUpdateChangesBtn(true);
    }

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        setEnableUpdateChangesBtn(true);
    }

    const handleGenderChange = (value) => {
        setGender(value);
        setEnableUpdateChangesBtn(true);
    }

    const handleDobChange = (date) => {
        setDob(date);
        setEnableUpdateChangesBtn(true);
    }

    const handleSelfIntroChange = (e) => {
        setSelfIntro(e.target.value);
        setEnableUpdateChangesBtn(true);
    }

    const handleEmploymentStatusChange = (selectedItem) => {
        setSelectedEmploymentStatus(selectedItem);
        setEnableUpdateChangesBtn(true);
    }

    const handleHighestEducationChange = (selectedItem) => {
        setSelectedHighestEducation(selectedItem);
        setEnableUpdateChangesBtn(true);
    }

    const handleGraduationYearChange = (date) => {
        setGraduationYear(date);
        setEnableUpdateChangesBtn(true);
    }

    const resetVerifyEmailToast = () => {
        setShowVerifyEmailMessage(false);
    }

    const resetSuccessMessages = () => {
        setSuccessMessageHeader('');
        setSuccessMessageDescription('');
    }

    const resetErrorMessages = () => {
        resetVerifyEmailToast();
        setErrorMessageHeader('');
        setErrorMessageDescription('');
    }

    const uploadPicture = async () => {
        let uploadSuccessful = true;
        try {
            resetSuccessMessages();
            resetErrorMessages();
            setIsLoading(true);

            const { userId, username, token } = getUserInfoAndToken();
            const response = await uploadProfilePicture(userId, username, token, profilePicture);
            if (response && response?.code === '00') {
                const data = response.data;
                setProfilePicture(null);
                setProfilePictureUrl('');
            } else {
                const messages = response?.message;
                if (messages?.length > 0) {
                    setErrorMessageHeader('Update error');
                    setErrorMessageDescription(messages.join('\n'));
                }
                uploadSuccessful = false;
                setEnableUpdateChangesBtn(true);
            }
        } catch (error) {
            console.log(`error occurred while uploading the profile picture: ${error}`);

            uploadSuccessful = false;
            setIsLoading(false);
            setErrorMessageHeader(SOMETHING_WENT_WRONG_MESSAGE);
            setErrorMessageDescription(PROFILE_PICTURE_UPLOAD_FAILED_MESSAGE);
        }
        return uploadSuccessful;
    }

    const handleUpdateChanges = async () => {
        if (selfIntro?.length > 1000) {
            setErrorMessageHeader('Update error');
            setErrorMessageDescription('Please keep Introduce yourself to under 1000 characters');
            return
        }

        //  Upload profile picture.
        let continueUpdate = true;
        if (profilePicture) {
            continueUpdate = await uploadPicture();
        }

        // If upload profile picture is successful, continue updating the remaining profile details.
        if (continueUpdate) {
            try {
                resetSuccessMessages();
                resetErrorMessages();
                setIsLoading(true);

                const updateFormParams = {
                    fullm: name,
                    phone: phoneNumber,
                    eml: email,
                };

                if (gender) {
                    const gv = codeTableGenderOptions.filter((g) => g.val === gender);
                    if (gv.length > 0) {
                        updateFormParams.gender = gv[0]?.codeid;
                    }
                }

                if (dob) {
                    if (dob?.toString().includes('-')) {
                        const dateObj = parseDate(dob);
                        updateFormParams.brthdt = formatDateForAPI(dateObj);
                    } else {
                        updateFormParams.brthdt = formatDateForAPI(dob);
                    }
                } else {
                    updateFormParams.brthdt = '';
                }

                const updateQueryParams = {
                    empsts: selectedEmploymentStatus?.codeid,
                    edulvlc: selectedHighestEducation?.codeid,
                    eduyoc: graduationYear && getYearFromDate(graduationYear),
                    dsc: selfIntro,
                    isavl: user?.isavl,
                };

                const { userId, username, token } = getUserInfoAndToken();
                const response = await updateProfile(userId, username, token, updateQueryParams, updateFormParams);
                if (response?.code === '00') {
                    logEvent(analytics, 'profile_about_me_updated', { 'userid': userId, 'employment_status': selectedEmploymentStatus?.val, 'gender': updateFormParams.gender, 'birthday': updateFormParams.brthdt, 'education_level': selectedHighestEducation?.val });
                    const data = response?.data;
                    if (storedUserInfo) {
                        storedUserInfo.userName = data[0].dispm;
                        storedUserInfo.userEmail = data[0].usrm;
                        storedUserInfo.pic = `${data[0].pic}&cb=${new Date().getTime()}`;
                    }
                    localStorage.setItem('user', JSON.stringify(storedUserInfo));
                    if (data?.length > 0) {
                        setUser(data[0]);
                    }
                    setProfilePictureUrl(`${data[0].pic}&cb=${new Date().getTime()}`)
                    setSuccessMessageHeader(UPDATE_SUCCESS_MESSAGE);
                    setSuccessMessageDescription('Your changes to About me has been saved!');
                    resetErrorMessages();
                    setEnableUpdateChangesBtn(false);
                } else {
                    const messages = response?.message;
                    if (messages?.length > 0) {
                        setErrorMessageHeader('Update error');
                        setErrorMessageDescription(messages.join('\n'));
                    }
                    setEnableUpdateChangesBtn(true);
                }

                setIsLoading(false);
            } catch (error) {
                console.log(`error occurred while updating the about me section: ${error}`);

                setIsLoading(false);
                setErrorMessageHeader(SOMETHING_WENT_WRONG_MESSAGE);
                setErrorMessageDescription(TRY_UPDATE_CHANGES_AGAIN_MESSAGE);
            }
        }
    }

    const [showVerifyEmailMessage, setShowVerifyEmailMessage] = useState(false);
    const handleVerifyEmailAddress = async () => {
        try {
            resetErrorMessages();
            setIsLoading(true);

            const { userId, username, token } = getUserInfoAndToken();
            const response = await verifyEmail(userId, username, token);
            if (response?.code === '00' || response?.isSuccess === 1) {
                setShowVerifyEmailMessage(true);
            } else {
                setShowVerifyEmailMessage(false);
                const messages = response?.message;
                if (messages?.length > 0) {
                    setErrorMessageHeader('Update error');
                    setErrorMessageDescription(messages.join('\n'));
                }
            }

            setIsLoading(false);
        } catch (error) {
            console.log(`error occurred while verifying the email: ${error}`);

            setIsLoading(false);
            setShowVerifyEmailMessage(false);
            setErrorMessageHeader(SOMETHING_WENT_WRONG_MESSAGE);
            setErrorMessageDescription(TRY_UPDATE_CHANGES_AGAIN_MESSAGE);
        }
    }

    const handleVerifyMobile = () => {
        navigate("/verifyMobileNumber", { state: { phoneNumber, showOtpScreen: true } })
    }

    return (
        <>
            {isLoading && <Loader></Loader>}
            {
                (successMessageHeader && successMessageDescription) && <Toast
                    type='success'
                    header={successMessageHeader}
                    message={successMessageDescription}
                    onClose={resetSuccessMessages}
                />
            }
            {
                showVerifyEmailMessage && <Toast
                    type='info'
                    header='Verification Email Sent'
                    message='Please check your inbox for the email and click on the verification link inside to complete the verification process'
                    onClose={resetVerifyEmailToast}
                />
            }
            {
                (errorMessageHeader && errorMessageDescription) && <Toast
                    type='error'
                    header={errorMessageHeader}
                    message={errorMessageDescription}
                    onClose={resetErrorMessages}
                />
            }
            {/* <div className='fab-profile-section'> */}
            <>
                <p className='fab-profile-section-header'>About me</p>
                <div className='fab-profile-section-picture-container'>
                    <p className='fab-profile-section-picture-header'>Profile picture</p>
                    <div className='fab-profile-section-picture-box'>
                        {
                            profilePictureUrl ?
                                <img ref={imgRef} className='fab-profile-custom-picture-container' src={profilePictureUrl} alt='' />
                                :
                                <svg className='fab-profile-section-picture' width='160' height='160' viewBox='0 0 160 160' fill='none' xmlns='http://www.w3.org/2000/svg' xmlnsXlink='http://www.w3.org/1999/xlink'>
                                    <rect width='160' height='160' fill='url(#pattern0)' />
                                    <defs>
                                        <pattern id='pattern0' patternContentUnits='objectBoundingBox' width='1' height='1'>
                                            <use xlinkHref='#image0_1147_53941' transform='translate(-0.0580357) scale(0.00446429)' />
                                        </pattern>
                                        <image id='image0_1147_53941' width='250' height='224' xlinkHref='data:image/jpeg;base64,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' />
                                    </defs>
                                </svg>
                        }
                        <label className='fab-profile-section-picture-button'>
                            <input type='file' onChange={handleProfilePictureChange} />
                            <p className='fab-profile-section-picture-button-text'>
                                Change profile picture
                            </p>
                        </label>
                    </div>
                </div>
                <div className='fab-profile-section-textfield-container'>
                    <p className='fab-profile-section-field-header'>Name</p>
                    <input className='fab-profile-section-textfield-input' defaultValue={name} value={name} onChange={handleNameChange} />
                </div>
                <div className='fab-profile-section-row'>
                    <div className='fab-profile-section-row-container'>
                        <p className='fab-profile-section-row-textfield-header'>Phone number</p>
                        {
                            phoneNumberVerified ?
                                <TextFieldSuccess defaultValue={phoneNumber} value={phoneNumber} onChangeHandler={handlePhoneNumberChange} />
                                :
                                <TextFieldError defaultValue={phoneNumber} value={phoneNumber} onChangeHandler={handlePhoneNumberChange} onClickVerifyHandler={handleVerifyMobile} />
                        }
                    </div>
                    <div className='fab-profile-section-row-container'>
                        <p className='fab-profile-section-row-textfield-header'>Email</p>
                        {
                            emailVerified ?
                                <TextFieldSuccess defaultValue={email} value={email} onChangeHandler={handleEmailChange} />
                                :
                                <TextFieldError defaultValue={email} value={email} onChangeHandler={handleEmailChange} onClickVerifyHandler={handleVerifyEmailAddress} disableInput={true} />
                        }
                    </div>
                </div>
                <div className='fab-profile-section-dropdown-container'>
                    <p className='fab-profile-section-field-header'>Employment status</p>
                    <div className='fab-dropdown'>
                        <Select
                            options={codeTableEmploymentStatusOptions}
                            isMulti={false}
                            getOptionValue={(option) => option.val}
                            getOptionLabel={(option) => option.val}
                            placeholder='Please select'
                            onChange={handleEmploymentStatusChange}
                            value={selectedEmploymentStatus}
                        />
                    </div>
                </div>
                <div className='fab-profile-section-row fab-education-graduate'>
                    <div className='fab-profile-section-row-container'>
                        <p className='fab-profile-section-field-header'>Highest education</p>
                        <div className='fab-dropdown'>
                            <Select
                                options={codeTableHighestEducationOptions}
                                isMulti={false}
                                getOptionValue={(option) => option.val}
                                getOptionLabel={(option) => option.val}
                                placeholder='Please select'
                                onChange={handleHighestEducationChange}
                                value={selectedHighestEducation}
                            />
                        </div>
                    </div>
                    <div className='fab-profile-section-row-container'>
                        <p className='fab-profile-section-field-header'>Graduation year (expected)</p>
                        <DatePicker
                            className={`fb-form-wp-date-picker-input-wrapper ${graduationYearErrorMessage ? 'fb-form-wp-date-picker-input-wrapper-error' : ''} fb-form-wp-date-picker-input-text`}
                            placeholderText='YYYY'
                            dateFormat='yyyy'
                            selected={graduationYear}
                            onChange={(date) => handleGraduationYearChange(date)}
                            minDate={minGraduationYear}
                            maxDate={maxGraduationYear}
                            showYearPicker
                            scrollableYearDropdown
                        />
                    </div>
                </div>
                <div className='fab-profile-section-row'>
                    <div className='fab-profile-section-row-container'>
                        <p className='fab-profile-section-field-header'>Gender</p>
                        <div className='fab-profile-section-gender-row'>
                            <div className='fab-profile-section-gender-option'>
                                <input className='fab-profile-section-gender-input' id='male' type='radio' value='Male' checked={gender === 'Male'} onChange={() => handleGenderChange('Male')} />
                                <label className='fab-profile-section-gender-text'>Male</label>
                            </div>
                            <div className='fab-profile-section-gender-option'>
                                <input className='fab-profile-section-gender-input' id='female' type='radio' value='Female' checked={gender === 'Female'} onChange={() => handleGenderChange('Female')} />
                                <label className='fab-profile-section-gender-text'>Female</label>
                            </div>
                        </div>
                    </div>
                    <div className='fab-profile-section-row-container'>
                        <p className='fab-profile-section-field-header'>Birthday</p>
                        <div className='fab-profile-section-dob-container'>
                            <DatePicker
                                className='fab-profile-section-dob-input-wrapper fab-profile-section-dob-input-text'
                                placeholderText='DD/MM/YYYY'
                                dateFormat='dd/MM/yyyy'
                                selected={dob}
                                onChange={(date) => handleDobChange(date)}
                                maxDate={currentDate}
                            />
                        </div>
                        {dobBackDateErrorMessage && <label className='fab-form-field-error-message'>{dobBackDateErrorMessage}</label>}
                    </div>
                </div>
                <div className='fab-profile-section-textarea-container'>
                    <span className='fab-profile-section-field-header'>Introduce yourself
                        <p className='fab-profile-section-field-header-optional'>(Optional)</p>
                    </span>
                    <textarea className='fab-profile-section-textarea-wrapper fab-profile-section-textarea-text' placeholder='Let your potential employer know who you are at a glance by adding in a short introduction.' value={selfIntro} onChange={handleSelfIntroChange} />
                </div>
                <div className='fab-profile-section-update-button-container'>
                    <Button className={`fab-profile-section-update-button ${enableUpdateChangesBtn ? '' : 'fab-profile-section-update-button-disable'}`} onClick={handleUpdateChanges} isDisabled={!enableUpdateChangesBtn}>
                        <p className='fab-profile-section-update-button-text'>Update changes</p>
                    </Button>
                </div>
            </>
            {/* </div > */}
        </>
    )
}

export default AboutMe;
